<template>
  <CGInput
    :id="id"
    :class="{
      'search-bar': true,
      'v-has-value': modelValue?.length > 0,
      disabled,
    }"
    :modelValue="modelValue"
    :placeholder="placeholder"
    :name="name"
    :errored="errored"
    :error-message="errorMessage"
    :label="label"
    :large="large"
    :disabled="disabled"
    type="search"
    unstyled-suffix
    @update:modelValue="handleInput"
  >
    <template v-if="$slots.prefix" #prefix>
      <slot name="prefix" />
    </template>
    <template #suffix>
      <div class="search-bar-suffix">
        <Spinner v-if="searching" />
        <button class="search-bar-cancel" @click="handleInput('')">
          <Icon name="close" :size="18" class="text-primary-300" />
        </button>
      </div>
    </template>
  </CGInput>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({

  props: {
    modelValue: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: undefined,
    },
    name: {
      type: String,
      default: undefined,
    },
    label: {
      type: String,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    errored: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    searching: {
      type: Boolean,
      default: false,
    },
    large: {
      type: Boolean,
      default: false,
    },
    debounceDuration: {
      type: Number,
      default: 700,
    },
  },

  emits: ['input', 'search', 'update:modelValue'],

  data() {
    return {
      debounceTimeout: null, // Store the timeout for debouncing
    };
  },

  methods: {
    /**
     * Handles the input event and emits debounced `search` events.
     * @param {string} value - The input value.
     */
    handleInput(value) {
      // Emit the raw input for `v-model` binding
      this.$emit('update:modelValue', value);

      // Clear any existing debounce timer
      clearTimeout(this.debounceTimeout);

      // Set a debounce timeout to emit the `search` event
      this.debounceTimeout = setTimeout(() => {
        this.$emit('search', value);
      }, this.debounceDuration);
    }
  }
});
</script>

<style lang="scss" scoped>
.search-bar {
  &.disabled {
    @apply cursor-not-allowed;

    .icon {
      @apply text-primary-300;
    }
  }
}

.search-bar-cancel {
  @apply hidden;

  .v-has-value:not(.disabled) & {
    @apply inline-flex;
  }
}

.search-bar-suffix {
  @apply flex items-center space-x-1;
  @apply pl-1 pr-2;
}
</style>

<style lang="scss" global>
.search-bar {
  input::-webkit-search-cancel-button {
    @apply hidden;
  }
}
</style>
